// Admin-Page für CSV/ZIP-Import + Daten-Export.
//
// Flow Import:
//   1. Datei (CSV oder ZIP) auswählen → POST /import/detect
//   2. Pro erkannter Datei: Ziel-Tabelle wählen + Spalten mappen (Drag-Drop
//      CSV-Spalte → Feld). Bereits gespeicherte Presets vorausfüllen.
//   3. 'Probelauf' (dryRun) oder 'Import starten' → POST /import/run
//   4. Summary zeigen.
//
// Mapping pro Ziel-Tabelle: jede Embertime-Pflicht-/Optional-Feld ist ein
// Drop-Target. Mapping-Wert kann CSV-Header sein oder { fixed: 'value' }.
// Speichern als Preset → wird beim nächsten Upload vorgeschlagen.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Download, FileUp, Save, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { TabNav, tabPillClass } from '../components/ui/tab-nav'
import { TableShell } from '../components/ui/table-shell'
import { API_BASE_URL, api, apiFetch, type ImportPreset } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/import-export',
  component: ImportExportPage,
})

type TabId = 'import' | 'export'

/** Felder pro Ziel-Tabelle. Der Importer (Backend) liest diese gleiche Liste
 *  via fieldMapping[fieldName] → csv-header. Pflichtfelder sind markiert. */
const FIELDS: Record<string, { key: string; label: string; required?: boolean; hint?: string }[]> =
  {
    users: [
      { key: 'externalId', label: 'externe ID', hint: 'für FK-Auflösung in time_entries/teams' },
      { key: 'name', label: 'Name', required: true },
      { key: 'email', label: 'E-Mail', required: true },
      { key: 'role', label: 'Rolle (admin/user)' },
      { key: 'active', label: 'Aktiv (1/0)' },
      { key: 'personnelNumber', label: 'Personalnummer' },
    ],
    customers: [
      { key: 'externalId', label: 'externe ID' },
      { key: 'name', label: 'Name', required: true },
      { key: 'active', label: 'Aktiv (1/0)' },
      { key: 'notes', label: 'Notizen' },
    ],
    projects: [
      { key: 'externalId', label: 'externe ID' },
      { key: 'name', label: 'Name', required: true },
      { key: 'customerExternalId', label: 'Kunden ext. ID', required: true },
      { key: 'active', label: 'Aktiv (1/0)' },
      { key: 'description', label: 'Beschreibung' },
    ],
    services: [
      { key: 'externalId', label: 'externe ID' },
      { key: 'name', label: 'Name', required: true },
      { key: 'active', label: 'Aktiv (1/0)' },
    ],
    time_entries: [
      { key: 'userExternalId', label: 'User ext. ID', required: true },
      { key: 'projectExternalId', label: 'Projekt ext. ID' },
      { key: 'serviceExternalId', label: 'Service ext. ID' },
      { key: 'description', label: 'Beschreibung' },
      { key: 'startTime', label: 'Start (ISO)', required: true },
      { key: 'endTime', label: 'Ende (ISO)' },
      { key: 'durationSeconds', label: 'Dauer (Sek.)' },
    ],
    teams: [
      { key: 'externalId', label: 'externe ID' },
      { key: 'name', label: 'Name', required: true },
      { key: 'leaderExternalId', label: 'Teamleiter ext. ID' },
    ],
    vacation_entitlements: [
      { key: 'userExternalId', label: 'User ext. ID', required: true },
      { key: 'year', label: 'Jahr', required: true },
      { key: 'days', label: 'Tage', required: true },
    ],
    vacation_adjustments: [
      { key: 'userExternalId', label: 'User ext. ID', required: true },
      { key: 'year', label: 'Jahr', required: true },
      { key: 'days', label: 'Tage', required: true },
      { key: 'description', label: 'Beschreibung' },
    ],
    hour_adjustments: [
      { key: 'userExternalId', label: 'User ext. ID', required: true },
      { key: 'date', label: 'Datum (optional)' },
      { key: 'year', label: 'Jahr (für Fallback)' },
      { key: 'hours', label: 'Stunden', required: true },
      { key: 'description', label: 'Beschreibung' },
    ],
    hour_adjustments_reduced: [
      { key: 'userExternalId', label: 'User ext. ID', required: true },
      { key: 'date', label: 'Datum' },
      { key: 'hours', label: 'Stunden (werden negiert)', required: true },
      { key: 'description', label: 'Beschreibung' },
    ],
    work_schedules: [
      { key: 'userExternalId', label: 'User ext. ID', required: true },
      { key: 'validFrom', label: 'Gültig ab', required: true },
      { key: 'validTo', label: 'Gültig bis' },
      { key: 'monHours', label: 'Mo Std.' },
      { key: 'tueHours', label: 'Di Std.' },
      { key: 'wedHours', label: 'Mi Std.' },
      { key: 'thuHours', label: 'Do Std.' },
      { key: 'friHours', label: 'Fr Std.' },
      { key: 'satHours', label: 'Sa Std.' },
      { key: 'sunHours', label: 'So Std.' },
    ],
    user_holiday_groups: [
      { key: 'userExternalId', label: 'User ext. ID', required: true },
      { key: 'groupName', label: 'Feiertagsgruppe', required: true },
    ],
    absences: [
      { key: 'userExternalId', label: 'User ext. ID', required: true },
      { key: 'fromDate', label: 'Datum ab', required: true },
      { key: 'toDate', label: 'Datum bis' },
      { key: 'type', label: 'Typ (Urlaub/Krank/…)', required: true },
      { key: 'days', label: 'Anzahl Tage (für ½)', hint: '0,5 → Halbtag' },
      { key: 'notes', label: 'Notiz' },
    ],
  }

interface DetectedTable {
  filename: string
  guessTable: string | null
  columns: string[]
  rowCount: number
  sample: Record<string, string>[]
}

interface TableConfig {
  targetTable: string
  mapping: Record<string, string>
  enabled: boolean
}

function ImportExportPage() {
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const [tab, setTab] = useState<TabId>('import')
  // Mobile-Shell (<640): Titel oben, Minimal-Bar unten — Tabs + Submits
  // bleiben im Content (Admin-Werkzeug, Mapping §3).
  useMobilePageTitle('Import / Export')

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (meData.user.role !== 'admin') {
    return (
      <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
        <p className="text-text-secondary">Import/Export ist nur für Admins zugänglich.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Einreihiger Standard-Kopf (Paket C): keep-row — Titel <640 in der
          Shell-Top-Leiste, die Tab-Buttons bleiben auch mobil im Content. */}
      <PageHeader
        title="Import / Export"
        phoneMode="keep-row"
        inline={
          <TabNav ariaLabel="Import/Export-Bereiche">
            {(['import', 'export'] as const).map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={tab === t}
                onClick={() => setTab(t)}
                className={tabPillClass(tab === t)}
              >
                {t === 'import' ? 'Import' : 'Export'}
              </button>
            ))}
          </TabNav>
        }
      />

      {tab === 'import' && <ImportSection />}
      {tab === 'export' && <ExportSection />}
    </div>
  )
}

function ImportSection() {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [tables, setTables] = useState<DetectedTable[]>([])
  const [configs, setConfigs] = useState<Record<string, TableConfig>>({})
  const [defaultPassword, setDefaultPassword] = useState('embertime-import-2026')
  const [summary, setSummary] = useState<
    | {
        filename: string
        targetTable: string
        inserted: number
        updated: number
        skipped: number
        errors: string[]
      }[]
    | null
  >(null)

  const { data: presetsData } = useQuery({
    queryKey: ['import-presets'],
    queryFn: () => api.listImportPresets(),
  })
  const presets = presetsData?.presets ?? []

  const detect = useMutation({
    mutationFn: (f: File) => api.importDetect(f),
    onSuccess: (data) => {
      setTables(data.tables)
      // Pre-fill configs from guessTable + presets, falls vorhanden.
      const initial: Record<string, TableConfig> = {}
      for (const t of data.tables) {
        const target = t.guessTable && FIELDS[t.guessTable] ? t.guessTable : ''
        const preset = presets.find(
          (p) => p.sourceFilename === t.filename && p.targetTable === target,
        )
        initial[t.filename] = {
          targetTable: target,
          mapping:
            target && preset ? guessMapping(t, target, preset) : guessMapping(t, target, null),
          enabled: !!target,
        }
      }
      setConfigs(initial)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const run = useMutation({
    mutationFn: (opts: { dryRun: boolean }) => {
      if (!file) throw new Error('Keine Datei')
      const mappingPayload: Record<
        string,
        { targetTable: string; mapping: Record<string, unknown> }
      > = {}
      for (const [fn, cfg] of Object.entries(configs)) {
        if (!cfg.enabled || !cfg.targetTable) continue
        mappingPayload[fn] = { targetTable: cfg.targetTable, mapping: cfg.mapping }
      }
      return api.importRun(file, mappingPayload, { defaultPassword, dryRun: opts.dryRun })
    },
    onSuccess: (data) => {
      setSummary(data.summary)
      notify.success(`${data.dryRun ? 'Probelauf' : 'Import'} fertig`)
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const savePreset = useMutation({
    mutationFn: (vars: { filename: string; cfg: TableConfig }) =>
      api.saveImportPreset({
        sourceFilename: vars.filename,
        targetTable: vars.cfg.targetTable,
        label: null,
        mapping: vars.cfg.mapping,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-presets'] })
      notify.success('Vorlage gespeichert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  function onFile(f: File | null) {
    setFile(f)
    setTables([])
    setConfigs({})
    setSummary(null)
    if (f) detect.mutate(f)
  }

  return (
    <div className="space-y-4">
      <Dropzone file={file} onFile={onFile} loading={detect.isPending} />

      {file && tables.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="space-y-1">
              <label
                htmlFor="default-pw"
                className="block text-xs uppercase tracking-wider text-text-tertiary"
              >
                Standard-Passwort (für neue User)
              </label>
              <Input
                id="default-pw"
                value={defaultPassword}
                onChange={(e) => setDefaultPassword(e.target.value)}
                className="w-64"
              />
            </div>
            <div className="ml-auto flex gap-2">
              <Button
                variant="secondary"
                onClick={() => run.mutate({ dryRun: true })}
                disabled={run.isPending}
              >
                Probelauf
              </Button>
              <Button onClick={() => run.mutate({ dryRun: false })} disabled={run.isPending}>
                <Upload className="h-4 w-4" />
                Import starten
              </Button>
            </div>
          </div>

          <div className="space-y-3">
            {tables.map((t) => {
              const cfg = configs[t.filename]
              if (!cfg) return null
              return (
                <TableConfigCard
                  key={t.filename}
                  table={t}
                  cfg={cfg}
                  onChange={(next) => setConfigs((prev) => ({ ...prev, [t.filename]: next }))}
                  onSavePreset={() => savePreset.mutate({ filename: t.filename, cfg })}
                  presets={presets}
                />
              )
            })}
          </div>
        </div>
      )}

      {summary && <ImportSummary summary={summary} />}
    </div>
  )
}

function Dropzone({
  file,
  onFile,
  loading,
}: {
  file: File | null
  onFile: (f: File | null) => void
  loading: boolean
}) {
  const [drag, setDrag] = useState(false)
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag-drop drop target needs onDrag
    <div
      onDragOver={(e) => {
        e.preventDefault()
        setDrag(true)
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDrag(false)
        if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0])
      }}
      className={cn(
        'rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors',
        drag
          ? 'border-accent bg-accent/5'
          : 'border-border-default bg-bg-elevated/30 hover:border-border-default/80',
      )}
    >
      <FileUp className="h-10 w-10 mx-auto mb-3 text-text-tertiary" />
      <p className="text-sm text-text-secondary mb-2">
        CSV oder ZIP hier ablegen — oder per Klick auswählen
      </p>
      <label className="inline-block cursor-pointer">
        <input
          type="file"
          accept=".csv,.zip,.txt"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <span className="inline-flex items-center gap-1.5 rounded-md bg-accent/15 text-accent border border-accent/30 px-3 py-1.5 text-xs font-medium">
          Datei wählen
        </span>
      </label>
      {file && (
        <p className="mt-3 text-xs text-text-tertiary">
          <span className="text-text-primary">{file.name}</span> · {(file.size / 1024).toFixed(1)}{' '}
          KB
          {loading && ' · analysiere…'}
        </p>
      )}
    </div>
  )
}

function TableConfigCard({
  table,
  cfg,
  onChange,
  onSavePreset,
  presets,
}: {
  table: DetectedTable
  cfg: TableConfig
  onChange: (next: TableConfig) => void
  onSavePreset: () => void
  presets: ImportPreset[]
}) {
  const fields = cfg.targetTable ? (FIELDS[cfg.targetTable] ?? []) : []
  const matchingPreset = presets.find(
    (p) => p.sourceFilename === table.filename && p.targetTable === cfg.targetTable,
  )

  return (
    <div
      className={cn(
        'rounded-2xl border bg-bg-elevated/40 overflow-hidden',
        cfg.enabled ? 'border-border-subtle' : 'border-border-subtle/60 opacity-60',
      )}
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => onChange({ ...cfg, enabled: e.target.checked })}
          className="h-4 w-4 accent-accent"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">{table.filename}</p>
          <p className="text-2xs text-text-tertiary">
            {table.rowCount} Zeilen · {table.columns.length} Spalten
            {matchingPreset && ' · Vorlage geladen'}
          </p>
        </div>
        <Select
          value={cfg.targetTable}
          onChange={(e) =>
            onChange({
              ...cfg,
              targetTable: e.target.value,
              mapping: guessMapping(table, e.target.value, null),
            })
          }
          className="w-auto min-w-[180px] text-xs"
        >
          <option value="">— Ziel wählen —</option>
          {Object.keys(FIELDS).map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
      </div>

      {cfg.enabled && cfg.targetTable && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 py-4">
          <div>
            <p className="mb-2 text-2xs uppercase tracking-wider text-text-tertiary">Felder</p>
            <div className="space-y-1.5">
              {fields.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={cfg.mapping[f.key]}
                  columns={table.columns}
                  sample={table.sample[0]}
                  onChange={(v) =>
                    onChange({
                      ...cfg,
                      mapping: { ...cfg.mapping, [f.key]: v ?? '' },
                    })
                  }
                />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-2xs uppercase tracking-wider text-text-tertiary">
              CSV-Spalten (drag → links auf Feld)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {table.columns.map((col) => (
                <DragColumn key={col} col={col} sample={table.sample[0]?.[col] ?? null} />
              ))}
            </div>
          </div>
        </div>
      )}

      {cfg.enabled && cfg.targetTable && (
        <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-border-subtle bg-bg-overlay/30">
          <Button variant="ghost" size="sm" onClick={onSavePreset}>
            <Save className="h-3.5 w-3.5" />
            Als Vorlage speichern
          </Button>
        </div>
      )}
    </div>
  )
}

function DragColumn({ col, sample }: { col: string; sample: string | null }) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drag source for column mapping
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/csv-col', col)
        e.dataTransfer.effectAllowed = 'copy'
      }}
      title={sample ? `Beispiel: ${sample}` : undefined}
      className="cursor-grab active:cursor-grabbing inline-flex items-center gap-1 rounded-md border border-border-subtle bg-bg-elevated px-2 py-1 text-[11px] text-text-secondary hover:border-accent/40 hover:text-text-primary transition-colors"
    >
      <span className="font-mono">{col}</span>
    </div>
  )
}

function FieldRow({
  field,
  value,
  columns,
  sample,
  onChange,
}: {
  field: { key: string; label: string; required?: boolean }
  value: string | undefined
  columns: string[]
  sample: Record<string, string> | undefined
  onChange: (v: string | null) => void
}) {
  const [drag, setDrag] = useState(false)
  const sampleVal = value && sample ? sample[value] : null

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target for column mapping
    <div
      onDragOver={(e) => {
        e.preventDefault()
        setDrag(true)
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDrag(false)
        const col = e.dataTransfer.getData('text/csv-col')
        if (col) onChange(col)
      }}
      className={cn(
        'flex items-center gap-2 rounded-md border px-2 py-1.5 transition-colors',
        drag
          ? 'border-accent bg-accent/5'
          : value
            ? 'border-accent/30 bg-accent/[0.03]'
            : 'border-border-subtle',
      )}
    >
      <span className="flex-1 min-w-0 text-xs">
        <span className="text-text-primary">{field.label}</span>
        {field.required && <span className="text-danger ml-0.5">*</span>}
      </span>
      <Select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-44 h-7 text-[11px]"
      >
        <option value="">— nicht setzen —</option>
        {columns.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </Select>
      {sampleVal != null && (
        <span className="text-2xs text-text-tertiary truncate max-w-[120px]">
          „{String(sampleVal).slice(0, 24)}“
        </span>
      )}
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onChange(null)}
          aria-label="Entfernen"
          className="h-7 w-7 text-text-tertiary hover:text-danger"
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  )
}

function guessMapping(
  table: DetectedTable,
  targetTable: string,
  preset: ImportPreset | null,
): Record<string, string> {
  if (preset) {
    // Aus dem Preset alle string-Werte übernehmen.
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(preset.mapping)) {
      if (typeof v === 'string') out[k] = v
    }
    return out
  }
  if (!targetTable || !FIELDS[targetTable]) return {}
  // Heuristik: case-insensitive Match auf Feld-Label oder bekannte clockodo-Header.
  const guesses: Record<string, string[]> = {
    externalId: ['Id', 'ID'],
    name: ['Name', 'Kundenname', 'Projektname', 'Leistungsname', 'Mitarbeiter', 'Teamname'],
    email: ['E-Mail-Adresse', 'E-Mail', 'Email'],
    role: ['Zugriffsrolle', 'Rolle', 'Role'],
    active: ['Aktiv', 'Active'],
    personnelNumber: ['Personalnummer'],
    notes: ['Notiz', 'Notes'],
    description: ['Notiz', 'Beschreibung', 'Description'],
    customerExternalId: ['Kunden-Id'],
    userExternalId: ['Mitarbeiter-Id'],
    projectExternalId: ['Projekt-Id'],
    serviceExternalId: ['Leistungs-Id'],
    startTime: ['Anfangszeit', 'Start'],
    endTime: ['Endzeit', 'End'],
    durationSeconds: ['Dauer'],
    leaderExternalId: ['Teamleiter-Id'],
    year: ['Jahr', 'Jahr von'],
    days: ['Anzahl Urlaubstage', 'Tage'],
    hours: ['Anzahl Stunden', 'Stunden'],
    date: ['Datum', 'Date'],
    validFrom: ['Gültig ab'],
    validTo: ['Gültig bis'],
    monHours: ['Montag'],
    tueHours: ['Dienstag'],
    wedHours: ['Mittwoch'],
    thuHours: ['Donnerstag'],
    friHours: ['Freitag'],
    satHours: ['Samstag'],
    sunHours: ['Sonntag'],
    groupName: ['Feiertagsgruppe'],
  }
  const out: Record<string, string> = {}
  for (const f of FIELDS[targetTable]) {
    const candidates = guesses[f.key] ?? []
    for (const cand of candidates) {
      const match = table.columns.find((c) => c.trim().toLowerCase() === cand.toLowerCase())
      if (match) {
        out[f.key] = match
        break
      }
    }
  }
  return out
}

function ImportSummary({
  summary,
}: {
  summary: {
    filename: string
    targetTable: string
    inserted: number
    updated: number
    skipped: number
    errors: string[]
  }[]
}) {
  return (
    <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
      <p className="px-4 py-2 text-xs uppercase tracking-wider text-text-tertiary border-b border-border-subtle">
        Ergebnis
      </p>
      <TableShell className="rounded-none border-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-bg-elevated/60 text-[11px] uppercase text-text-tertiary">
              <th className="px-3 py-2 text-left">Datei</th>
              <th className="px-3 py-2 text-left">Ziel</th>
              <th className="px-3 py-2 text-right">+</th>
              <th className="px-3 py-2 text-right">↺</th>
              <th className="px-3 py-2 text-right">↷</th>
              <th className="px-3 py-2 text-left">Fehler</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((s) => (
              <tr key={s.filename} className="border-t border-border-subtle">
                <td className="px-3 py-2 text-text-primary">{s.filename}</td>
                <td className="px-3 py-2 text-text-secondary font-mono text-[11px]">
                  {s.targetTable}
                </td>
                <td className="px-3 py-2 text-right text-success font-mono tabular-nums">
                  {s.inserted}
                </td>
                <td className="px-3 py-2 text-right text-text-secondary font-mono tabular-nums">
                  {s.updated}
                </td>
                <td className="px-3 py-2 text-right text-text-tertiary font-mono tabular-nums">
                  {s.skipped}
                </td>
                <td className="px-3 py-2 text-[11px] text-danger">
                  {s.errors.length > 0 ? (
                    <details>
                      <summary>{s.errors.length} Fehler</summary>
                      <ul className="mt-1 list-disc pl-4 max-h-32 overflow-y-auto">
                        {s.errors.slice(0, 10).map((e) => (
                          <li key={e} className="break-all">
                            {e}
                          </li>
                        ))}
                        {s.errors.length > 10 && <li>… +{s.errors.length - 10}</li>}
                      </ul>
                    </details>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  )
}

function ExportSection() {
  const [busy, setBusy] = useState<'export' | 'import' | null>(null)
  const [result, setResult] = useState<string | null>(null)
  const [wipe, setWipe] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleExport = async () => {
    setBusy('export')
    setResult(null)
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/export/embertime`, { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `embertime-export-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      setResult(`✓ Export erfolgreich heruntergeladen (${(blob.size / 1024 / 1024).toFixed(2)} MB)`)
    } catch (e) {
      setResult(`✗ Fehler: ${(e as Error).message}`)
    } finally {
      setBusy(null)
    }
  }

  const handleImport = async () => {
    if (!pendingFile) return
    setBusy('import')
    setResult(null)
    try {
      const form = new FormData()
      form.append('file', pendingFile)
      const res = await apiFetch(
        `${API_BASE_URL}/api/import/embertime${wipe ? '?wipe=true' : ''}`,
        {
          method: 'POST',
          body: form,
          credentials: 'include',
        },
      )
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`)
      const data = (await res.json()) as {
        wipe: boolean
        summary: Array<{ table: string; inserted: number; errors: number }>
      }
      const total = data.summary.reduce((s, x) => s + x.inserted, 0)
      const errors = data.summary.reduce((s, x) => s + x.errors, 0)
      setResult(
        `✓ Import abgeschlossen — ${total} Zeilen eingefügt, ${errors} Fehler. Wipe: ${data.wipe ? 'ja' : 'nein'}`,
      )
      setPendingFile(null)
    } catch (e) {
      setResult(`✗ Fehler: ${(e as Error).message}`)
    } finally {
      setBusy(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border-default bg-bg-elevated/30 p-6">
        <h3 className="text-base font-semibold text-text-primary mb-2">Embertime-zu-Embertime</h3>
        <p className="text-sm text-text-secondary mb-4">
          JSON-Dump aller Daten (User, Time-Entries, Absences, Schedules, etc.) — direkt auf eine
          andere Embertime-Instanz übertragbar. Sessions/Mail/Push bleiben außen vor.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" onClick={handleExport} disabled={busy !== null}>
            <Download className="h-4 w-4" />
            {busy === 'export' ? 'Exportiere…' : 'Export herunterladen'}
          </Button>

          <label className="inline-flex items-center gap-2 rounded-lg border border-border-default bg-bg-elevated px-4 py-2 text-sm font-medium text-text-primary hover:bg-bg-hover cursor-pointer">
            {pendingFile ? 'Andere Datei…' : 'Import-Datei wählen'}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) {
                  setPendingFile(f)
                  setResult(null)
                }
              }}
            />
          </label>

          <Button type="button" onClick={handleImport} disabled={busy !== null || !pendingFile}>
            <Upload className="h-4 w-4" />
            {busy === 'import' ? 'Importiere…' : 'Import starten'}
          </Button>

          <label className="inline-flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={wipe}
              onChange={(e) => setWipe(e.target.checked)}
              className="rounded border-border-default"
            />
            DB vor Import wipen (⚠️ alle Daten verloren)
          </label>
        </div>
        {pendingFile && (
          <div className="mt-3 text-xs text-text-secondary">
            Gewählt: <span className="font-medium text-text-primary">{pendingFile.name}</span> (
            {(pendingFile.size / 1024 / 1024).toFixed(2)} MB)
          </div>
        )}
        {result && (
          <div
            className={`mt-4 rounded-lg px-4 py-3 text-sm ${
              result.startsWith('✓')
                ? 'bg-green-500/10 text-green-300'
                : 'bg-red-500/10 text-red-300'
            }`}
          >
            {result}
          </div>
        )}
      </div>
    </div>
  )
}
